import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminCouponRedemptionsListAction } from "../Action/action";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  }
  return String(value);
}

function moneyLabel(amount, currency) {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  const cur = currency ? String(currency).toUpperCase() : "";
  return cur ? `${cur} ${n.toFixed(2)}` : n.toFixed(2);
}

function discountRuleLabel(row) {
  if (row?.coupon_discount == null) return "—";
  const n = Number(row.coupon_discount);
  if (!Number.isFinite(n)) return "—";
  return String(row.coupon_type) === "amount" ? moneyLabel(n, row.currency) : `${n}%`;
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "badge-success";
  if (s === "pending") return "badge-warning";
  if (s === "cancelled" || s === "canceled") return "badge-danger";
  if (s === "expired") return "badge-muted";
  return "badge-muted";
}

function titleCaseStatus(status) {
  if (!status) return "—";
  const s = String(status);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/** Show first/last 6 chars only, e.g. abcdef...uvwxyz */
function maskPurchaseToken(token) {
  if (token == null || token === "") return "—";
  const s = String(token);
  if (s.length <= 12) return s;
  return `${s.slice(0, 6)}...${s.slice(-6)}`;
}

export default function CouponRedemptions() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 10 });

  const [activeRow, setActiveRow] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [query, status]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminCouponRedemptionsListAction({
          page,
          per_page: perPage,
          search: query.trim() || undefined,
          status: status === "All" ? undefined : status.toLowerCase()
        });
        if (cancelled) return;
        if (!res?.success) {
          setRows([]);
          setMeta({ total: 0, page, per_page: perPage });
          setError(res?.message || res?.msg || "Failed to load applied coupons.");
          return;
        }
        setRows(Array.isArray(res.data) ? res.data : []);
        setMeta({
          total: Number(res?.meta?.total || 0),
          page: Number(res?.meta?.page || page),
          per_page: Number(res?.meta?.per_page || perPage)
        });
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setMeta({ total: 0, page, per_page: perPage });
        setError(e?.response?.data?.message || e?.message || "Failed to load applied coupons.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [page, perPage, query, status, reloadNonce]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setActiveRow(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const total = meta?.total ? Number(meta.total) : 0;
  const totalPages = total ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const start = total && rows.length ? (page - 1) * perPage + 1 : 0;
  const end = total && rows.length ? start + rows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const clearAndReload = () => {
    setQuery("");
    setStatus("All");
    setPage(1);
    setError("");
    setReloadNonce((n) => n + 1);
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Applied coupons unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="flex items-center gap-3" style={{ flexWrap: "wrap" }}>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search code, user, course, purchase id…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="filter-group">
            {["All", "Pending", "Completed", "Cancelled", "Expired"].map((v) => (
              <button
                key={v}
                className={`filter-btn ${status === v ? "active" : ""}`}
                type="button"
                onClick={() => setStatus(v)}
              >
                {v}
              </button>
            ))}
          </div>
          <button className="btn btn-outline" type="button" onClick={clearAndReload} disabled={loading}>
            ↻ Clear
          </button>
        </div>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Code</th>
                <th>User</th>
                <th>Course</th>
                <th>Original</th>
                <th>Payable</th>
                <th>Status</th>
                <th>Applied</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((r, index) => (
                  <tr key={r.id != null ? r.id : `row-${index}`}>
                    <td>{start + index}</td>
                    <td>
                      <strong>{r.coupon_code || "—"}</strong>
                    </td>
                    <td>
                      {r.user_id ? (
                        <Link to={`/users/${r.user_id}`} className="action-link">
                          <strong>{r.user_name || r.user_email || `User #${r.user_id}`}</strong>
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{r.course_title || "—"}</td>
                    <td>{moneyLabel(r.original_price, r.currency)}</td>
                    <td>{moneyLabel(r.discounted_price, r.currency)}</td>
                    <td>
                      <span className={`badge ${statusBadgeClass(r.status)}`}>
                        {titleCaseStatus(r.status)}
                      </span>
                    </td>
                    <td>{formatDateTime(r.created_on)}</td>
                    <td>
                      <div className="actions-cell">
                        <button
                          type="button"
                          className="action-link"
                          style={{ background: "transparent", border: "none", padding: 0 }}
                          onClick={() => setActiveRow(r)}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9}>
                    <div className="empty-state">
                      <div className="empty-icon">🎟️</div>
                      No applied coupons match your filters.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span>
            Showing {start}-{end} of {total} Applied Coupons
          </span>
          <div className="pagination-pages">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1 || loading}
              title="First page"
              aria-label="First page"
            >
              {"<<"}
            </button>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
              ‹
            </button>
            {pageButtons.map((p) => (
              <button
                key={p}
                className={p === page ? "active" : ""}
                type="button"
                onClick={() => setPage(p)}
                disabled={loading}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages || loading}
              title="Last page"
              aria-label="Last page"
            >
              {">>"}
            </button>
          </div>
        </div>
      </div>

      <div
        className={`modal-overlay ${activeRow ? "active" : ""}`}
        onClick={() => setActiveRow(null)}
      >
        <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Applied Coupon #{activeRow?.id || ""}</h3>
            <button className="modal-close" type="button" onClick={() => setActiveRow(null)}>
              ✕
            </button>
          </div>
          <div className="modal-body">
            {activeRow ? (
              <div className="info-grid" style={{ marginBottom: 0 }}>
                <div className="info-item">
                  <label>Status</label>
                  <p>
                    <span className={`badge ${statusBadgeClass(activeRow.status)}`}>
                      {titleCaseStatus(activeRow.status)}
                    </span>
                  </p>
                </div>
                <div className="info-item">
                  <label>Coupon Code</label>
                  <p>{activeRow.coupon_code || "—"}</p>
                </div>
                <div className="info-item">
                  <label>Coupon Name</label>
                  <p>{activeRow.coupon_name || "—"}</p>
                </div>
                <div className="info-item">
                  <label>Coupon Rule</label>
                  <p>
                    {titleCaseStatus(activeRow.coupon_type)} · {discountRuleLabel(activeRow)}
                  </p>
                </div>
                <div className="info-item">
                  <label>User</label>
                  <p>
                    {activeRow.user_id ? (
                      <Link to={`/users/${activeRow.user_id}`} className="action-link">
                        {activeRow.user_name || activeRow.user_email || `User #${activeRow.user_id}`}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </p>
                </div>
                <div className="info-item">
                  <label>Email</label>
                  <p>{activeRow.user_email || "—"}</p>
                </div>
                <div className="info-item">
                  <label>Course</label>
                  <p>
                    {activeRow.course_title ||
                      (activeRow.course_id ? `Course #${activeRow.course_id}` : "—")}
                  </p>
                </div>
                <div className="info-item">
                  <label>Original Price</label>
                  <p>{moneyLabel(activeRow.original_price, activeRow.currency)}</p>
                </div>
                <div className="info-item">
                  <label>Discount Amount</label>
                  <p>{moneyLabel(activeRow.discount_amount, activeRow.currency)}</p>
                </div>
                <div className="info-item">
                  <label>Payable / Discounted</label>
                  <p>{moneyLabel(activeRow.discounted_price, activeRow.currency)}</p>
                </div>
                <div className="info-item">
                  <label>Currency</label>
                  <p>{activeRow.currency || "—"}</p>
                </div>
                <div className="info-item">
                  <label>Purchase ID</label>
                  <p>{activeRow.purchase_id ?? "—"}</p>
                </div>
                <div className="info-item">
                  <label>Purchase Token</label>
                  <p style={{ wordBreak: "break-all" }}>{maskPurchaseToken(activeRow.purchase_token)}</p>
                </div>
                <div className="info-item">
                  <label>Coupon ID</label>
                  <p style={{ wordBreak: "break-all" }}>{activeRow.coupon_id || "—"}</p>
                </div>
                <div className="info-item">
                  <label>Applied On</label>
                  <p>{formatDateTime(activeRow.created_on)}</p>
                </div>
                <div className="info-item">
                  <label>Updated On</label>
                  <p>{formatDateTime(activeRow.updated_on)}</p>
                </div>
                <div className="info-item">
                  <label>Completed On</label>
                  <p>{formatDateTime(activeRow.completed_on)}</p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
